import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, CheckCircle, BarChart3, MapPin } from "lucide-react";
import "./AdminPage.css";
import axios from "axios";
import PopupAlert, { usePopup } from "./PopupAlert";

// ================================================

const AdminPage = () => {
  const navigate = useNavigate();
  const [time, setTime] = useState(new Date());
  const { popup, showPopup, showConfirm, closePopup, handleConfirm } =
    usePopup();

  const [formData, setFormData] = useState({
    name: "",
    state: "",
    rating: "",
    img: "",
  });

  const [fleetData, setFleetData] = useState({
    vehicleType: "",
    models: "",
    capacity: "",
    pricePerKm: "",
    dailyRate: "",
  });

  const [fleetList, setFleetList] = useState([]);

  const [onDemandData, setOnDemandData] = useState({
    title: "",
    price: "",
  });

  const [onDemandList, setOnDemandList] = useState([]);

  // Add this near your other state declarations
  const [editingId, setEditingId] = useState(null);
  const handleEditClick = (item) => {
    setEditingId(item._id);
    setFormData({
      name: item.name,
      state: item.state,
      rating: item.rating,
      img: item.img,
    });
    // Optional: Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Offers State
  const [offerData, setOfferData] = useState({
    title: "",
    description: "",
    couponCode: "",
    discountText: "",
  });
  const [offersList, setOffersList] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic URL Validation
    if (!formData.img.startsWith("http")) {
      showPopup("Please enter a valid image URL", "error");
      return;
    }

    const destinationData = {
      name: formData.name.trim(),
      state: formData.state.trim(),
      rating: Number(formData.rating),
      img: formData.img.trim(),
    };

    try {
      if (editingId) {
        // UPDATE EXISTING
        await axios.put(
          `/api/destinations/${editingId}`,
          destinationData,
        );
        showPopup("✨ Destination Updated Successfully!", "success");
      } else {
        // CREATE NEW
        await axios.post("/api/destinations", destinationData);
        showPopup("✨ Destination Published Successfully!", "success");
      }

      // Reset Form and State
      setFormData({ name: "", state: "", rating: "", img: "" });
      setEditingId(null);
      // Refresh your list here if you have a fetchDestinations() function
    } catch (error) {
      showPopup(
        `Operation Failed: ${error.response?.data?.message || "Server Error"}`,
        "error",
      );
    }
  };

  const handleFleetSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post("/api/fleet", fleetData);
      showPopup("✨ Fleet Item Added Successfully!", "success");
      setFleetData({
        vehicleType: "",
        models: "",
        capacity: "",
        pricePerKm: "",
        dailyRate: "",
      });
      fetchFleet();
    } catch (error) {
      showPopup("Failed to add fleet item", "error");
    }
  };

  const deleteFleetItem = async (id) => {
    showConfirm("Delete this fleet item?", async () => {
      try {
        await axios.delete(`/api/fleet/${id}`);
        fetchFleet();
      } catch (error) {
        showPopup("Failed to delete fleet item", "error");
      }
    });
  };

  const handleOnDemandSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post("/api/ondemand-tours", onDemandData);
      showPopup("✨ On-Demand Tour Added Successfully!", "success");
      setOnDemandData({ title: "", price: "" });
      fetchOnDemand();
    } catch (error) {
      showPopup("Failed to add tour", "error");
    }
  };

  const deleteOnDemandItem = async (id) => {
    showConfirm("Delete this tour?", async () => {
      try {
        await axios.delete(`/api/ondemand-tours/${id}`);
        fetchOnDemand();
      } catch (error) {
        showPopup("Failed to delete tour", "error");
      }
    });
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/api/offers", offerData);
      showPopup("✨ Offer Created Successfully!", "success");
      setOfferData({
        title: "",
        description: "",
        couponCode: "",
        discountText: "",
      });
      fetchOffers();
    } catch (error) {
      showPopup("Failed to create offer", "error");
    }
  };

  const deleteOffer = async (id) => {
    showConfirm("Delete this offer?", async () => {
      try {
        await axios.delete(`/api/offers/${id}`);
        fetchOffers();
      } catch (error) {
        showPopup("Failed to delete offer", "error");
      }
    });
  };

  // --- BACKEND STATE ---
  const [customerBookings, setCustomerBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBookings = async () => {
    try {
      setIsLoading(true);
      const response = await fetch("/api/bookings");
      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();
      setCustomerBookings(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Using mock data due to error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFleet = async () => {
    try {
      const response = await axios.get("/api/fleet");
      setFleetList(response.data);
    } catch (err) {
      console.error("Error fetching fleet:", err);
    }
  };

  const fetchOnDemand = async () => {
    try {
      const response = await axios.get("/api/ondemand-tours");
      setOnDemandList(response.data);
    } catch (err) {
      console.error("Error fetching tours:", err);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get("/api/offers");
      setOffersList(response.data);
    } catch (err) {
      console.error("Error fetching offers:", err);
    }
  };

  useEffect(() => {
    const isAuth = sessionStorage.getItem("isAdminAuthenticated");
    if (!isAuth) {
      navigate("/");
      return;
    }

    fetchBookings();
    fetchFleet();
    fetchOnDemand();
    fetchOffers();
    const timer = setInterval(() => setTime(new Date()), 1000);
    const dataRefresh = setInterval(fetchBookings, 30000);

    return () => {
      clearInterval(timer);
      clearInterval(dataRefresh);
    };
  }, []);

  const [reviewCount, setReviewCount] = useState(0);
  const API_URL_REVIEWS = "/api/reviews";

  useEffect(() => {
    const fetchReviewStats = async () => {
      try {
        const res = await axios.get(API_URL_REVIEWS);
        setReviewCount(res.data.length);
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };
    fetchReviewStats();
  }, []);

  return (
    <div className="admin-body">
      {/* Custom Popup */}
      <PopupAlert
        popup={popup}
        onClose={closePopup}
        onConfirm={handleConfirm}
      />

      <header className="admin-header">
        <div className="header-left">
          <motion.button
            whileHover={{ x: -4 }}
            onClick={() => navigate("/")}
            className="back-btn"
          >
            <ArrowLeft size={18} /> Back to Site
          </motion.button>
          <h1 className="admin-title">Admin Dashboard</h1>
        </div>

        <div className="header-right">
          <div className="live-indicator">
            <span className="pulse-dot"></span>
            <span className="live-text">SYSTEM LIVE</span>
          </div>
          <div className="real-time-clock">
            <span className="clock-time">{time.toLocaleTimeString()}</span>
            <span className="clock-date">{time.toLocaleDateString()}</span>
          </div>
        </div>
      </header>

      {/* Stats Grid */}
      <div className="admin-stats-grid">
        <div className="stat-box">
          <Users color="#3b82f6" size={24} />
          <p>Total Users</p>
          <h2>{reviewCount.toLocaleString()}</h2>
        </div>
        <div className="stat-box">
          <CheckCircle color="#10b981" size={24} />
          <p>Bookings</p>
          <h2>{customerBookings.length}</h2>
        </div>
        <div className="stat-box">
          <BarChart3 color="#f59e0b" size={24} />
          <p>Conversion</p>
          <h2>18.4%</h2>
        </div>
      </div>

      <div className="admin-grid-layout">
        <div className="admin-card">
          <div className="card-header">
            <h3>Booking Customer Details</h3>
            {isLoading && <span className="loading-text">Updating...</span>}
          </div>

          {error ? (
            <div className="error-message">Error: {error}</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Destination</th>
                  <th>Date</th>
                  <th>Number</th>
                </tr>
              </thead>
              <tbody>
                {customerBookings.length > 0 ? (
                  customerBookings.map((booking) => (
                    <tr key={booking._id || booking.id}>
                      <td>
                        <div className="user-info">
                          <span className="user-name">{booking.name}</span>
                          <span className="user-email">{booking.email}</span>
                        </div>
                      </td>
                      <td>
                        <div className="dest-info">
                          <MapPin size={14} /> {booking.location}
                        </div>
                      </td>
                      <td>
                        {booking.bookingDate
                          ? new Date(booking.bookingDate).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>
                        <span
                          className={`status-badge ${booking.status?.toLowerCase()}`}
                        >
                          {booking.number}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      style={{ textAlign: "center", padding: "2rem" }}
                    >
                      No bookings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="admin-container">
          <h2>Add New Destination</h2>
          <form onSubmit={handleSubmit} className="admin-form">
            <input
              type="text"
              placeholder="Destination Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="State"
              value={formData.state}
              onChange={(e) =>
                setFormData({ ...formData, state: e.target.value })
              }
              required
            />
            <input
              type="number"
              step="0.1"
              placeholder="Rating (e.g. 4.5)"
              value={formData.rating}
              onChange={(e) =>
                setFormData({ ...formData, rating: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Image URL (Unsplash link)"
              value={formData.img}
              onChange={(e) =>
                setFormData({ ...formData, img: e.target.value })
              }
              required
            />
            <button type="submit" className="btn-primary">
              Upload to DB
            </button>
          </form>
        </div>

        <div className="admin-container">
          <h2>Add Premium Fleet</h2>
          <form onSubmit={handleFleetSubmit} className="admin-form">
            <input
              type="text"
              placeholder="Vehicle Type (e.g., Sedan)"
              value={fleetData.vehicleType}
              onChange={(e) =>
                setFleetData({ ...fleetData, vehicleType: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Models (e.g., Swift Dzire, Toyota Etios)"
              value={fleetData.models}
              onChange={(e) =>
                setFleetData({ ...fleetData, models: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Capacity (e.g., 4+1)"
              value={fleetData.capacity}
              onChange={(e) =>
                setFleetData({ ...fleetData, capacity: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Price / KM"
              value={fleetData.pricePerKm}
              onChange={(e) =>
                setFleetData({ ...fleetData, pricePerKm: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Daily Rate"
              value={fleetData.dailyRate}
              onChange={(e) =>
                setFleetData({ ...fleetData, dailyRate: e.target.value })
              }
              required
            />
            <button type="submit" className="btn-primary">
              Add Fleet Item
            </button>
          </form>
        </div>

        <div className="admin-card" style={{ marginTop: "2rem" }}>
          <div className="card-header">
            <h3>Manage Premium Fleet</h3>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Models</th>
                <th>Capacity</th>
                <th>Price/KM</th>
                <th>Daily Rate</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {fleetList.length > 0 ? (
                fleetList.map((item) => (
                  <tr key={item._id}>
                    <td>{item.vehicleType}</td>
                    <td>{item.models}</td>
                    <td>{item.capacity}</td>
                    <td>{item.pricePerKm}</td>
                    <td>{item.dailyRate}</td>
                    <td>
                      <button
                        onClick={() => deleteFleetItem(item._id)}
                        style={{
                          color: "red",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    No fleet items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-container" style={{ marginTop: "2rem" }}>
          <h2>Add On-Demand Tour</h2>
          <form onSubmit={handleOnDemandSubmit} className="admin-form">
            <input
              type="text"
              placeholder="Tour Title (e.g., Talakona & Local)"
              value={onDemandData.title}
              onChange={(e) =>
                setOnDemandData({ ...onDemandData, title: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Price (e.g., ₹400)"
              value={onDemandData.price}
              onChange={(e) =>
                setOnDemandData({ ...onDemandData, price: e.target.value })
              }
              required
            />
            <button type="submit" className="btn-primary">
              Add Tour
            </button>
          </form>
        </div>

        <div className="admin-card" style={{ marginTop: "2rem" }}>
          <div className="card-header">
            <h3>Manage On-Demand Tours</h3>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tour Title</th>
                <th>Price</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {onDemandList.length > 0 ? (
                onDemandList.map((item) => (
                  <tr key={item._id}>
                    <td>{item.title}</td>
                    <td>{item.price}</td>
                    <td>
                      <button
                        onClick={() => deleteOnDemandItem(item._id)}
                        style={{
                          color: "red",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    No tours found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* --- OFFERS MANAGEMENT UI --- */}
        <div className="admin-container" style={{ marginTop: "2rem" }}>
          <h2>Create Promotional Offer</h2>
          <form onSubmit={handleOfferSubmit} className="admin-form">
            <input
              type="text"
              placeholder="Offer Title (e.g., Summer Special)"
              value={offerData.title}
              onChange={(e) =>
                setOfferData({ ...offerData, title: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Description (e.g., Get 20% off all temple tours)"
              value={offerData.description}
              onChange={(e) =>
                setOfferData({ ...offerData, description: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Discount Text (e.g., 20% OFF)"
              value={offerData.discountText}
              onChange={(e) =>
                setOfferData({ ...offerData, discountText: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Coupon Code (e.g., SUMMER20)"
              value={offerData.couponCode}
              onChange={(e) =>
                setOfferData({ ...offerData, couponCode: e.target.value })
              }
              required
            />
            <button type="submit" className="btn-primary">
              Launch Offer
            </button>
          </form>
        </div>

        <div
          className="admin-card"
          style={{ marginTop: "2rem", gridColumn: "1 / -1" }}
        >
          <div className="card-header">
            <h3>Active Promotions</h3>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Discount</th>
                <th>Coupon Code</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {offersList.length > 0 ? (
                offersList.map((item) => (
                  <tr key={item._id}>
                    <td>{item.title}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ background: "#f59e0b" }}
                      >
                        {item.discountText}
                      </span>
                    </td>
                    <td>
                      <strong>{item.couponCode}</strong>
                    </td>
                    <td>
                      <button
                        onClick={() => deleteOffer(item._id)}
                        style={{
                          color: "red",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    No active offers. Create one above!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
